import { ref, computed, watch, unref, getCurrentScope, onScopeDispose } from 'vue';
import {
	rangesUrl,
	recordingStatusUrl,
	mediaInfoUrl,
	normalizeRanges,
	mergeRanges,
	clipRangesToExtent,
	rangeAt as rangeAtHelper,
	inRange as inRangeHelper,
	nextRangeStart as nextRangeStartHelper,
	clampSelectionToRange as clampHelper,
} from './dvrUtils';

const LIVE_WINDOW_SEC = 300;
const LIVE_POLL_MS = 2500;
const MAX_POLL_MS = 20000;
const EXTENT_REFRESH_MS = 30000;
const VIEWPORT_DEBOUNCE_MS = 250;
const LIVE_THRESHOLD_SEC = 15;

// Reactive view over the Flussonic DVR ranges of one stream.
//
// - `baseUrl` may be a ref/computed; the composable resets and restarts when it
//   changes and idles while it is empty.
// - Polls the live window (now +/- 300s) every 2.5s, backing off to 20s on
//   errors. Viewport fetches are debounced 250ms via `setViewport(from, to)`.
// - `status`: 'idle' | 'loading' | 'ready' | 'empty' | 'unknown' | 'error'
//   ('empty' = stream known, nothing recorded yet; 'unknown' = origin has never
//   seen this stream).
export function useDvrRanges(baseUrl, options = {}) {
	const now = options.now || (() => Date.now() / 1000);

	const ranges = ref([]);
	const dvrExtent = ref(null); // { start, end } | null
	const status = ref('idle');
	const error = ref(null);
	const lastPolledAt = ref(0);
	// media_info.json: null until probed. The origin answers 404 while the
	// stream is not publishing, so `publishing` doubles as a liveness signal
	// even before the first range shows up.
	const mediaInfo = ref(null);
	const publishing = ref(null); // true | false | null (unknown)

	// Total kbps across the tracks the export includes (video + audio).
	const bitrateKbps = computed(() => {
		const tracks = mediaInfo.value?.tracks;
		if (!Array.isArray(tracks)) return null;
		let total = 0;
		for (const t of tracks) {
			if ((t?.content === 'video' || t?.content === 'audio') && typeof t.bitrate === 'number') total += t.bitrate;
		}
		return total > 0 ? total : null;
	});

	const liveEdge = computed(() => {
		let edge = null;
		for (const r of ranges.value) {
			if (edge === null || r.end > edge) edge = r.end;
		}
		return edge;
	});
	const hasDvr = computed(() => ranges.value.length > 0);
	const isLive = computed(() => {
		const edge = liveEdge.value;
		if (edge === null) return false;
		return edge >= now() - LIVE_THRESHOLD_SEC;
	});

	let stopped = true;
	let livePollTimer = null;
	let extentTimer = null;
	let viewportTimer = null;
	let livePollInterval = LIVE_POLL_MS;
	let generation = 0;
	let liveController = null;
	let viewportController = null;
	let pendingViewport = null;

	const base = () => unref(baseUrl);

	async function fetchJson(url, controller) {
		const res = await fetch(url, { signal: controller?.signal, cache: 'no-store' });
		if (!res.ok) {
			const err = new Error(`HTTP ${res.status}`);
			err.status = res.status;
			throw err;
		}
		return res.json();
	}

	// Follow `next` cursors until the origin stops returning one.
	async function fetchRanges(fromSec, toSec, controller) {
		const b = base();
		if (!b) return [];
		let cursor;
		let collected = [];
		for (let page = 0; page < 50; page += 1) {
			const data = await fetchJson(rangesUrl(b, fromSec, toSec, cursor), controller);
			collected = collected.concat(normalizeRanges(data?.ranges));
			cursor = data?.next;
			if (!cursor) break;
		}
		return collected;
	}

	function applyRanges(incoming) {
		let merged = mergeRanges(ranges.value, incoming);
		if (dvrExtent.value) merged = clipRangesToExtent(merged, dvrExtent.value);
		ranges.value = merged;
		if (merged.length > 0) status.value = 'ready';
		else if (status.value === 'loading' || status.value === 'ready') status.value = 'empty';
	}

	async function fetchExtent(controller) {
		const b = base();
		if (!b) return null;
		const data = await fetchJson(recordingStatusUrl(b), controller);
		const entry = data && typeof data === 'object' ? Object.values(data)[0] : null;
		if (entry && typeof entry.from === 'number' && typeof entry.to === 'number' && entry.to > entry.from) {
			return { start: entry.from, end: entry.to };
		}
		return null;
	}

	// Resolves true when the origin currently knows the stream (it is
	// publishing); also records the track list for size estimates.
	async function probeMediaInfo(controller) {
		const b = base();
		if (!b) return false;
		try {
			const res = await fetch(mediaInfoUrl(b), { signal: controller?.signal, cache: 'no-store' });
			if (!res.ok) {
				publishing.value = false;
				return false;
			}
			const data = await res.json();
			if (data && Array.isArray(data.tracks)) mediaInfo.value = data;
			publishing.value = true;
			return true;
		} catch (e) {
			if (e?.name !== 'AbortError') publishing.value = false;
			return false;
		}
	}

	async function initialLoad(gen) {
		status.value = 'loading';
		error.value = null;
		const controller = new AbortController();
		liveController = controller;
		try {
			const extent = await fetchExtent(controller);
			if (gen !== generation) return;
			dvrExtent.value = extent;
			if (extent) {
				const all = await fetchRanges(extent.start - 1, Math.max(extent.end, now()) + LIVE_WINDOW_SEC, controller);
				if (gen !== generation) return;
				applyRanges(all);
			}
			const known = await probeMediaInfo(controller);
			if (gen !== generation) return;
			if (ranges.value.length === 0) status.value = known ? 'empty' : 'unknown';
			lastPolledAt.value = now();
			livePollInterval = LIVE_POLL_MS;
		} catch (e) {
			if (e?.name === 'AbortError' || gen !== generation) return;
			error.value = e;
			status.value = ranges.value.length ? 'ready' : 'error';
			livePollInterval = Math.min(MAX_POLL_MS, livePollInterval * 2);
		}
		scheduleLivePoll(gen);
		scheduleExtentRefresh(gen);
	}

	function scheduleLivePoll(gen) {
		clearTimeout(livePollTimer);
		if (stopped || gen !== generation) return;
		livePollTimer = setTimeout(() => pollLive(gen), livePollInterval);
	}

	async function pollLive(gen) {
		if (stopped || gen !== generation) return;
		const controller = new AbortController();
		liveController = controller;
		const t = now();
		try {
			const incoming = await fetchRanges(t - LIVE_WINDOW_SEC, t + LIVE_WINDOW_SEC, controller);
			if (gen !== generation) return;
			applyRanges(incoming);
			if (ranges.value.length === 0 && status.value === 'error') status.value = 'empty';
			error.value = null;
			lastPolledAt.value = now();
			livePollInterval = LIVE_POLL_MS;
		} catch (e) {
			if (e?.name === 'AbortError' || gen !== generation) return;
			error.value = e;
			if (ranges.value.length === 0) status.value = 'error';
			livePollInterval = Math.min(MAX_POLL_MS, livePollInterval * 2);
		}
		scheduleLivePoll(gen);
	}

	function scheduleExtentRefresh(gen) {
		clearTimeout(extentTimer);
		if (stopped || gen !== generation) return;
		extentTimer = setTimeout(async () => {
			if (stopped || gen !== generation) return;
			try {
				const extent = await fetchExtent();
				if (gen !== generation) return;
				if (extent) {
					dvrExtent.value = extent;
					ranges.value = clipRangesToExtent(ranges.value, extent);
				}
				await probeMediaInfo();
				if (gen !== generation) return;
				if (ranges.value.length === 0 && status.value !== 'error') {
					status.value = publishing.value ? 'empty' : 'unknown';
				}
			} catch (e) {
				// transient; the live poll reports errors
			}
			scheduleExtentRefresh(gen);
		}, EXTENT_REFRESH_MS);
	}

	// Called by the timeline when the visible window changes.
	function setViewport(fromSec, toSec) {
		pendingViewport = { from: fromSec, to: toSec };
		clearTimeout(viewportTimer);
		viewportTimer = setTimeout(() => flushViewport(generation), VIEWPORT_DEBOUNCE_MS);
	}

	async function flushViewport(gen) {
		if (stopped || gen !== generation || !pendingViewport) return;
		const { from, to } = pendingViewport;
		pendingViewport = null;
		viewportController?.abort();
		const controller = new AbortController();
		viewportController = controller;
		try {
			const incoming = await fetchRanges(from, to, controller);
			if (gen !== generation) return;
			applyRanges(incoming);
		} catch (e) {
			// viewport fetches are best-effort
		}
	}

	function refresh() {
		if (stopped) return;
		clearTimeout(livePollTimer);
		livePollInterval = LIVE_POLL_MS;
		pollLive(generation);
	}

	function stop() {
		stopped = true;
		generation += 1;
		clearTimeout(livePollTimer);
		clearTimeout(extentTimer);
		clearTimeout(viewportTimer);
		liveController?.abort();
		viewportController?.abort();
	}

	function start() {
		stop();
		if (!base()) {
			status.value = 'idle';
			return;
		}
		stopped = false;
		ranges.value = [];
		dvrExtent.value = null;
		mediaInfo.value = null;
		publishing.value = null;
		error.value = null;
		initialLoad(generation);
	}

	watch(() => base(), () => start(), { immediate: true });

	if (getCurrentScope()) {
		onScopeDispose(stop);
	}

	return {
		ranges,
		dvrExtent,
		liveEdge,
		isLive,
		hasDvr,
		status,
		error,
		lastPolledAt,
		mediaInfo,
		publishing,
		bitrateKbps,
		inRange: (t) => inRangeHelper(ranges.value, t),
		rangeAt: (t) => rangeAtHelper(ranges.value, t),
		nextRangeStart: (t) => nextRangeStartHelper(ranges.value, t),
		clampSelectionToRange: (a, b, anchor) => clampHelper(ranges.value, a, b, anchor),
		setViewport,
		refresh,
		start,
		stop,
	};
}
